// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — TODO logic. Pure: no DOM, no jQuery, no network.
//
// Everything here is a function of its arguments so it can be tested without a
// browser, per the project's 3-layer rule (application layer must not touch the
// DOM) and the way `mylibs/` keeps its decidable parts pure on the PHP side.
// ─────────────────────────────────────────────────────────────────────────────

import {
    type Assignee,
    DUE_BUCKET_ORDER,
    LIFECYCLE_CATEGORIES,
    type LifecycleCategory,
    type LifecycleTransition,
    type RelationSummary,
    type StateCount,
    type Todo,
    type TodoComment,
    type TodoGroup,
    type TodoGrouping,
    TODO_GROUPINGS,
    type PpmKind,
    type PpmLink,
    type RelatedKind,
    type RelatedLink,
    type StateIcon,
    type ListScope,
    type TodoRelation,
} from "home/app/mobile/types";
import { dueBucketLabel, dueFacts } from "home/app/mobile/due";
import { currentLanguage, dateLocale, t } from "home/app/mobile/i18n";
import { TODO_PRIORITIES, priorityName, priorityOf } from "home/app/mobile/priorities";
import { relationListName, relationName } from "home/app/mobile/relationNames";

/**
 * The colour per CATEGORY, for a state the tenant gave no usable colour — as a THEME TOKEN.
 *
 * ⚠ The fallback, not the rule. A tenant's own state colour always wins (see `stateColor()`),
 * because it is the only thing that tells two states of one category apart — "In progress" and
 * "To be reviewed" are both ACTIVE.
 *
 * A token rather than a hex because these are OUR colours and must follow light and dark mode:
 * ACTIVE's navy is the dark ground's own colour. `mobile.scss` defines `--color-state-*` per theme.
 * A tenant's colour is theirs and stays as they chose it; the stylesheet lifts it in dark mode.
 */
const CATEGORY_COLORS: Readonly<Record<LifecycleCategory, string>> = {
    NEW: "var(--color-state-new)",
    PLANNING: "var(--color-state-new)",
    ACTIVE: "var(--color-state-active)",
    CLOSED: "var(--color-state-closed)",
};

/** The colour of a state with neither a colour nor a category. */
const FALLBACK_STATE_COLOR = "var(--color-state-new)";

/**
 * A category as PQFORCE's enum spells it, or null.
 *
 * The one reader for every category that arrives over the wire, so nothing downstream has to decide
 * what an unexpected spelling meant. Mirrors `pqfMobileStateCategory()` on the server.
 */
export function lifecycleCategoryOf(value: unknown): LifecycleCategory | null {
    const category = String(value ?? "")
        .trim()
        .toUpperCase();

    return (LIFECYCLE_CATEGORIES as readonly string[]).includes(category) ? (category as LifecycleCategory) : null;
}

/**
 * A tenant colour as `#RRGGBB`, or null when it is not one we can use.
 *
 * ⚠ HEX ONLY, and that is a SAFETY rule as much as a parsing one: the value is written into a
 * `style="…"` attribute (`stateDot()` in the UI), so anything else — a `;`, a `url(` — would be CSS the
 * tenant injected. Measured tenants store `#RRGGBB`; `#RGB` is accepted because it is the same
 * thing written shorter.
 */
export function hexColorOf(value: unknown): string | null {
    const text = String(value ?? "").trim();
    const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(text);
    if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toUpperCase();

    return /^#[0-9a-f]{6}$/i.test(text) ? text.toUpperCase() : null;
}

/** WCAG relative luminance of `#RRGGBB`. */
function luminanceOf(hex: string): number {
    const channel = (offset: number) => {
        const c = parseInt(hex.slice(offset, offset + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** WCAG contrast ratio of two `#RRGGBB` colours. */
export function contrastRatio(a: string, b: string): number {
    const [hi, lo] = [luminanceOf(a), luminanceOf(b)].sort((x, y) => y - x) as [number, number];
    return (hi + 0.05) / (lo + 0.05);
}

/** `#RRGGBB` mixed with `toward` by `share` (0–1) — what CSS `color-mix(in srgb, …)` computes. */
function mixHex(hex: string, toward: string, share: number): string {
    const part = (h: string, offset: number) => parseInt(h.slice(offset, offset + 2), 16);
    const out = [1, 3, 5].map((offset) =>
        Math.round(part(hex, offset) * (1 - share) + part(toward, offset) * share)
            .toString(16)
            .padStart(2, "0"),
    );
    return `#${out.join("")}`.toUpperCase();
}

/**
 * A tenant's state colour, DARKENED just enough to read on the light ground — or unchanged.
 *
 * The circle draws its icon and ring in the state's colour on a 14% tint of it (`.pqm-state`), and
 * a pale tenant colour (a light yellow, a sky blue) made a pale icon on a paler tint: under the 3:1
 * WCAG 1.4.11 asks of a graphic. CSS cannot measure contrast, so it is decided here: step toward
 * black until the colour reaches 3:1 against that tint. The hue stays the tenant's; a colour that
 * already reads is returned as it came. Dark mode lifts whatever this returns toward white in the
 * stylesheet, which a darker start only helps.
 */
export function legibleStateColor(hex: string): string {
    let color = hex.toUpperCase();
    for (let step = 0; step < 20; step++) {
        const tint = mixHex(color, "#FFFFFF", 0.86);
        if (contrastRatio(color, tint) >= 3) return color;
        color = mixHex(color, "#000000", 0.08);
    }
    return color;
}

/**
 * A state's colour: the tenant's own `#RRGGBB`, else its category's theme token, else the neutral one.
 *
 * The tenant's colour wins — it is the tenant's lifecycle, and its palette is the only thing that
 * distinguishes two states of one category. The answer is written into a `style` attribute as
 * `--state:…`, so it is only ever validated hex or one of our own constant tokens — never anything
 * the tenant typed. How the circle is drawn from it lives in `mobile.scss` (`.pqm-state`).
 */
export function stateColor(category: LifecycleCategory | null | undefined, tenantColor?: string | null): string {
    const own = hexColorOf(tenantColor);
    if (own) return legibleStateColor(own);

    const known = lifecycleCategoryOf(category);

    return known ? CATEGORY_COLORS[known] : FALLBACK_STATE_COLOR;
}

/**
 * The text on a transition button: the TARGET STATE's name, always.
 *
 * ⚠ NEVER THE TRANSITION'S OWN NAME, by decision (2026-09-22). It was preferred when set, with
 * "Move to {state}" as the fallback — so one tenant's buttons read "Zur Abnahme" beside "Move to
 * Blocked", depending only on which transitions somebody had happened to name in the lifecycle
 * designer (and "New transition" is PQFORCE's default there). The state is what the user is
 * choosing; the button also draws it as the list will paint it afterwards, icon and colour.
 *
 * The transition name still has ONE use: a self-transition is the comment field, not a move, and
 * its name is that field's placeholder — see commentPlaceholder().
 */
export function transitionLabel(transition: LifecycleTransition): string {
    // ⚠ A self-transition never reaches here — `movesOf()` filters it out, because it is not a
    // move at all: it IS the comment field. See selfTransitionOf() below.
    return transition.to;
}

/**
 * The self-transition, if this user has one — the thing that makes commenting possible.
 *
 * ⚠ A self-transition (`from.id === to.id`) IS THE COMMENT FIELD, not a move. PQFORCE has no
 * "comment" call on a project item, so a tenant models one as a transition that goes nowhere — the
 * measured tenant calls it "Add log entry" and runs Init → Init — and the server already posts a
 * comment as exactly that (`pqfMobileComment()` finds it and fires `Upgrade/{id}?remark=`).
 *
 * It used to be rendered as an "Add note" BUTTON beside the real moves. The design refused that
 * explicitly: "it is never a button and never opens the transition sheet … a comment IS that
 * transition." Two things follow, and the second is the load-bearing one:
 *
 *   • the move list must not show it, or the screen offers two ways to do one thing;
 *   • **the right to comment is the right to fire it**, so the comment field appears only when the
 *     API returned one for this user. The API returns only PERMITTED transitions, so its absence is
 *     a permission fact, not a rendering choice.
 */
export function selfTransitionOf(todo: Todo): LifecycleTransition | null {
    return todo.transitions.find((t) => t.selfTransition === true) ?? null;
}

/** The transitions that actually MOVE the item — what the buttons are built from. */
export function movesOf(todo: Todo): LifecycleTransition[] {
    return todo.transitions.filter((t) => t.selfTransition !== true);
}

/**
 * The placeholder in the comment field: the tenant's own word for its self-transition.
 *
 * A tenant that named it "Add log entry" or "Notiz erfassen" is describing what the field does
 * better than we can, and it is the same string the button used to carry.
 */
export function commentPlaceholder(transition: LifecycleTransition | null): string {
    return transition?.label?.trim() || t("mobileTextWriteComment", "Write a comment");
}

/**
 * Why the activity log is read-only, when it is.
 *
 * ⚠ Distinct from the server's 422, which says the INSTALLATION has no self-transition configured
 * at all. This one says THIS USER may not fire the one that exists, right now — usually because the
 * item is with the other party. Collapsing the two would tell a customer to reconfigure their
 * tenant when the real answer is that they are waiting on a colleague.
 */
export function noCommentNote(todo: Todo): string {
    const holder = counterparty(todo).name;

    return holder
        ? t("mobileTextLogWithHolder", "You cannot add to the log while this TODO is with {holder}.", { holder })
        : t("mobileTextLogWithSomebody", "You cannot add to the log while this TODO is with somebody else.");
}

/**
 * The hint under the transition comment box.
 *
 * ⚠ A note is ALWAYS optional. Moving to a state bound as "blocked" used to require one; with the
 * state bindings gone nothing identifies such a state, and PQFORCE's transitions carry no "remark
 * required" flag to take its place. The server no longer refuses a move without a note either.
 */
export function transitionNoteHint(): string {
    return t("mobileTextTransitionNoteHint", "Optional. Anything you write is stored with the transition.");
}

/**
 * What to say when the API offers no transitions at all.
 *
 * This is a normal state, not an error — an assignee whose item is awaiting review is waiting on the
 * owner — so while the TODO is still open it names WHO is holding it rather than reading like a
 * permission failure. It used to say "to accept it", which was true only of a state bound as
 * "review"; without that binding the app cannot know the owner is being asked to accept anything,
 * only that the next move is theirs.
 *
 * A CLOSED todo gets the plain sentence: nobody is holding a finished item.
 */
export function noTransitionNote(todo: Todo): string {
    if (todo.relation === "assignee" && todo.statusCategory !== "CLOSED") {
        // The owner may be unknown when the tenant has bound no owner relation —
        // "Waiting for  ." is worse than not naming anybody.
        return todo.owner
            ? t("mobileTextWaitingFor", "Waiting for {owner}. You cannot move it further.", { owner: todo.owner })
            : t("mobileTextWaitingForOwner", "Waiting for its owner. You cannot move it further.");
    }
    return t("mobileTextNoFurtherTransition", "Your relation to this TODO allows no further transition.");
}

/** Up to two initials for an avatar. Safe on empty and single-word names. */
export function initialsOf(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0]!)
        .join("")
        .slice(0, 2)
        .toUpperCase();
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * An activity timestamp: `25 Aug, 14:20` — or `25. Aug., 14:20` in German.
 *
 * ⚠ Deliberately NOT `formatTimestamp()` from `home/app/basic/posts`, which is
 * the portal's one formatter and renders `DD.MM.YYYY HH:mm`. Two reasons, and
 * the second is the binding one: the design specifies this shorter form for a
 * conversation log, and that module imports `home/data`, `editor` and
 * `general_functions`, so reusing it would pull the desktop app layer into a
 * bundle that a phone downloads. Written out rather than delegated to
 * `toLocaleString`, which would render differently per device locale for a
 * string the design fixes.
 */
export function formatActivityTime(ts: number): string {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    // English from the fixed table, byte-identical to the design; German through Intl, in the app's
    // language rather than the phone's — see `app/mobile/i18n.ts`.
    if (currentLanguage() === "en") return `${d.getDate()} ${MONTHS[d.getMonth()]}, ${time}`;

    return `${new Intl.DateTimeFormat(dateLocale(), { day: "numeric", month: "short" }).format(d)}, ${time}`;
}

/** Newest first. Returns a new array — the caller's list is not reordered. */
export function sortComments(comments: readonly TodoComment[]): TodoComment[] {
    return [...comments].sort((a, b) => b.ts - a.ts);
}

/** A comment is a state change when it names a target that differs from its source. */
export function isStateChange(comment: TodoComment): boolean {
    return !!comment.to && comment.to !== comment.from;
}

/**
 * The todos this relation reaches.
 *
 * ⚠ `relations`, not `relation`. Someone who both owns a todo and is assigned it holds two, and the
 * item belongs under BOTH headings — which is what is true of it. It used to arrive twice, once per
 * relation, and the second copy was unreachable because a todo is found by id.
 */
export function todosForRelation(todos: readonly Todo[], relation: TodoRelation): Todo[] {
    return todos.filter((t) => t.relations.includes(relation));
}

/**
 * Is a TODO OPEN? Its lifecycle state's category is anything but `CLOSED` — the one rule the app
 * uses wherever it says "open" (the list's count, the home block). A state with no category is
 * open: nothing says it is finished.
 */
export function isOpenTodo(todo: Pick<Todo, "statusCategory">): boolean {
    return todo.statusCategory !== "CLOSED";
}

/** How many of these TODOs are open — see `isOpenTodo()`. */
export function openCount(todos: readonly Todo[]): number {
    return todos.filter(isOpenTodo).length;
}

/**
 * The rows the list shows for its segment. "All" is the tenant's own list (`allTodos`, empty until
 * it has loaded) with this person's own copy of a TODO in place of the "All" row wherever there is
 * one — that copy is whole (transitions, history), the "All" row is `partial`.
 */
export function todosForScope(todos: readonly Todo[], allTodos: readonly Todo[] | null, scope: ListScope): Todo[] {
    if (scope !== "all") return todosForRelation(todos, scope);
    const own = new Map(todos.map((todo) => [todo.id, todo]));
    return (allTodos ?? []).map((todo) => own.get(todo.id) ?? todo);
}

/**
 * The lifecycle order of a todo's state, for sorting. A state with no order sorts after every state
 * that has one — it has to appear somewhere, and inventing a position would be a guess.
 */
function stateRank(todo: Todo): number {
    return typeof todo.statusOrder === "number" ? todo.statusOrder : Number.POSITIVE_INFINITY;
}

/** Ascending lifecycle order; ties by name, so two states the tenant left unordered still sort predictably. */
function byLifecycleOrder(a: Todo, b: Todo): number {
    const ra = stateRank(a);
    const rb = stateRank(b);
    if (ra !== rb) return ra < rb ? -1 : 1;

    return a.status.localeCompare(b.status);
}

/** What a todo is grouped under when grouping by status: its state id, or its name from an older server. */
function stateKey(todo: Todo): string {
    return todo.statusId || `name:${todo.status}`;
}

/** The grouping after this one, for the list's button: due → status → priority → project → due. */
export function nextGrouping(grouping: TodoGrouping): TodoGrouping {
    const at = TODO_GROUPINGS.indexOf(grouping);
    return TODO_GROUPINGS[(at + 1) % TODO_GROUPINGS.length] ?? "due";
}

/**
 * Group the list for display.
 *
 * Empty groups are dropped, so the headings describe what is actually below
 * them. Grouping by status shows every state the list's todos are in, in the
 * tenant's own lifecycle order. Grouping by priority runs High → Medium → Low,
 * then the TODOs with none — last, because they are most of the list and the
 * least urgent reading of it. Grouping by project is alphabetical, then the
 * TODOs with no project — see `projectGroups()`.
 */
export function groupTodos(todos: readonly Todo[], grouping: TodoGrouping, now: number): TodoGroup[] {
    // ⚠ ONE instant for the whole list. Deriving per todo from its own `Date.now()` would let a
    // list straddle midnight and put two todos with the same date in different groups.
    /**
     * ⚠ WITHIN a group, by lifecycle order and then by title — never the order the API happened to
     * return. That order is not stable between two reads, so a list could reshuffle under
     * somebody's thumb between one refresh and the next with nothing having changed.
     *
     * Lifecycle order first, so a due group reads the way the tenant's process runs; title second,
     * which is the only tie-break a reader can predict. When grouping BY status the state is
     * constant inside a group, so this degrades to title alone.
     *
     * `localeCompare` rather than `<`: these are the tenant's own words, and umlauts sort as
     * letters rather than as codepoints.
     */
    const sortItems = (items: Todo[]) => items.sort((a, b) => byLifecycleOrder(a, b) || a.title.localeCompare(b.title));

    if (grouping === "due") {
        return DUE_BUCKET_ORDER.map((label) => {
            const items = sortItems(todos.filter((t) => dueFacts(t.dueISO, now).bucket === label));

            // ⚠ The KEY keeps the bucket value — it is what a device remembers as folded, and it
            // must not change with the language. Only the heading is translated. `due` is what the
            // heading's chip is coloured by.
            return { key: `due:${label}`, label: dueBucketLabel(label), due: label, count: items.length, items };
        }).filter((g) => g.items.length > 0);
    }

    if (grouping === "project") return projectGroups(todos, sortItems);

    if (grouping === "priority") {
        // Highest first — the reverse of the PQFORCE client's own low-to-high list, because a list
        // grouped by priority is read for what to do first. An unknown value is "none", as everywhere.
        const levels = [...TODO_PRIORITIES].reverse();
        const byLevel = levels.map((priority) => {
            const items = sortItems(todos.filter((t) => priorityOf(t.priority)?.id === priority.id));
            return {
                key: `priority:${priority.id}`,
                label: priorityName(priority.id),
                priority: priority.id,
                count: items.length,
                items,
            };
        });
        const none = sortItems(todos.filter((t) => !priorityOf(t.priority)));

        return [
            ...byLevel,
            {
                key: "priority:none",
                label: t("mobileViewNoPriorityGroup", "No priority"),
                count: none.length,
                items: none,
            },
        ].filter((g) => g.items.length > 0);
    }

    // Grouped by the state itself (its id), headed by the tenant's name for it, in the tenant's
    // lifecycle order. Keyed on the id so two states that happen to share a name stay two groups.
    const groups = new Map<string, Todo[]>();
    for (const todo of todos) {
        const key = stateKey(todo);
        groups.set(key, [...(groups.get(key) ?? []), todo]);
    }

    return [...groups.values()]
        .sort((a, b) => byLifecycleOrder(a[0]!, b[0]!))
        .map((items) => {
            const first = items[0]!;
            return {
                key: `status:${stateKey(first)}`,
                label: first.status,
                // Read off the group's first TODO: grouped by the state's id, they all share it.
                state: {
                    status: first.status,
                    statusCategory: first.statusCategory,
                    statusColor: first.statusColor,
                    statusIcon: first.statusIcon,
                },
                count: items.length,
                items: sortItems(items),
            };
        });
}

/**
 * The project a TODO belongs to, as far as this app can say: `{id, name}`, either of which may be
 * empty, or null for none.
 *
 * ⚠ THE PPM LINK FIRST — "Linked to" — and its `projectId` is what identifies the project. A task
 * link names the project it sits in; a portfolio link names no project at all. Only a TODO with no
 * project there falls back to a risk or project report ("Related to"), which carries the project's
 * NAME but no id — so that answer has an empty id.
 */
export function todoProject(todo: Todo): { id: string; name: string } | null {
    const ppm = todo.ppm;
    if (ppm) {
        const id = ppm.projectId ?? "";
        const name = ppm.kind === "project" ? ppm.name : ppm.kind === "task" ? ppm.project : "";
        if (id || name) return { id, name };
    }

    const related = (todo.related ?? []).find((link) => link.project);
    return related ? { id: "", name: related.project } : null;
}

/**
 * The list grouped by project: one group per project, alphabetically, then "No project".
 *
 * ⚠ KEYED BY THE PROJECT'S ID, never its name, wherever the id is known — two projects may share a
 * name, and one heading over both would put one project's work under the other. A TODO known only
 * by a project NAME (a risk or report link) joins the group of the one project in this list with
 * that name; where no project or several carry it, it gets a group of its own under that name,
 * rather than being filed under a project it may not belong to.
 *
 * A project whose name could not be read is still a project: headed "Unnamed project", one group
 * per id, sorted after the named ones.
 */
function projectGroups(todos: readonly Todo[], sortItems: (items: Todo[]) => Todo[]): TodoGroup[] {
    const byKey = new Map<string, { label: string; named: boolean; items: Todo[] }>();
    const add = (key: string, label: string, named: boolean, todo: Todo) => {
        const group = byKey.get(key) ?? { label, named, items: [] };
        group.items.push(todo);
        byKey.set(key, group);
    };

    // Which ids carry each name, among the TODOs whose project is known by id.
    const idsByName = new Map<string, Set<string>>();
    for (const todo of todos) {
        const project = todoProject(todo);
        if (project?.id && project.name) {
            idsByName.set(project.name, (idsByName.get(project.name) ?? new Set()).add(project.id));
        }
    }

    const none: Todo[] = [];
    for (const todo of todos) {
        const project = todoProject(todo);
        if (!project) {
            none.push(todo);
            continue;
        }
        if (project.id) {
            add(
                `project:id:${project.id}`,
                project.name || t("mobileViewUnnamedProject", "Unnamed project"),
                !!project.name,
                todo,
            );
            continue;
        }
        const ids = idsByName.get(project.name);
        if (ids && ids.size === 1) add(`project:id:${[...ids][0]}`, project.name, true, todo);
        else add(`project:name:${project.name}`, project.name, true, todo);
    }

    const groups = [...byKey.entries()]
        .sort(([, a], [, b]) => (a.named === b.named ? a.label.localeCompare(b.label) : a.named ? -1 : 1))
        .map(([key, group]) => ({
            key,
            label: group.label,
            ...(key.startsWith("project:id:") ? { projectId: key.slice("project:id:".length) } : {}),
            count: group.items.length,
            items: sortItems(group.items),
        }));

    if (none.length) {
        groups.push({
            key: "project:none",
            label: t("mobileViewNoProjectGroup", "No project"),
            count: none.length,
            items: sortItems(none),
        });
    }

    return groups;
}

/**
 * Collapse a group that is open, or open one that is collapsed. Returns a new list.
 *
 * The collapsed set is a list of `TodoGroup.key`s rather than a flag on the group, because groups
 * are rebuilt from the todos on every render — a flag would not survive the next one.
 */
export function toggleCollapsedGroup(collapsed: readonly string[], key: string): string[] {
    return collapsed.includes(key) ? collapsed.filter((k) => k !== key) : [...collapsed, key];
}

/**
 * Fold every one of `keys` away (`collapse`), or open every one of them. Returns a new list.
 *
 * ⚠ ONLY THE GIVEN KEYS. The caller passes the groups on screen, and a key belonging to another
 * grouping — or to a group the search has hidden — keeps whatever its user left it as. "Collapse
 * all" is about what is in front of you, not a reset of every fold the device remembers.
 */
export function setGroupsCollapsed(collapsed: readonly string[], keys: readonly string[], collapse: boolean): string[] {
    if (!collapse) return collapsed.filter((key) => !keys.includes(key));

    return [...collapsed, ...keys.filter((key) => !collapsed.includes(key))];
}

/** Whether the list's all-groups button should FOLD — true while any of these groups is open. */
export function anyGroupOpen(collapsed: readonly string[], keys: readonly string[]): boolean {
    return keys.some((key) => !collapsed.includes(key));
}

/**
 * The collapsed set as stored on the device, or `[]`.
 *
 * Defensive because it is whatever localStorage held: junk, another version's shape, or nothing.
 * Capped, because status keys carry tenant state ids and a device paired with several
 * installations would otherwise grow the list for ever.
 */
export function parseCollapsedGroups(raw: string | null): string[] {
    try {
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) return [];
        const keys = parsed.filter(
            (k): k is string => typeof k === "string" && /^(due|status|priority|project):/.test(k),
        );

        return [...new Set(keys)].slice(-MAX_COLLAPSED_GROUPS);
    } catch {
        return [];
    }
}

/** How many collapsed groups a device remembers. Oldest are forgotten first. */
export const MAX_COLLAPSED_GROUPS = 100;

/** The figures on one home tile. */
export function summariseRelation(todos: readonly Todo[], relation: TodoRelation, now: number): RelationSummary {
    const list = todosForRelation(todos, relation);

    // One count per OPEN state that holds something, in lifecycle order. CLOSED is the one category
    // read for meaning here, and only to leave finished work out of a tile about what is on your plate.
    const open = new Map<string, Todo[]>();
    for (const todo of [...list].sort(byLifecycleOrder)) {
        if (todo.statusCategory === "CLOSED") continue;
        const key = stateKey(todo);
        open.set(key, [...(open.get(key) ?? []), todo]);
    }
    const states: StateCount[] = [...open.values()].map((items) => ({
        label: items[0]!.status,
        count: items.length,
    }));

    return {
        total: list.length,
        overdue: list.filter((t) => dueFacts(t.dueISO, now).overdue).length,
        states,
    };
}

/**
 * A home tile's sub-line: the overdue count, then each open state's count.
 *
 * "3 overdue · 4 In progress · 1 Blocked". The overdue figure is always there, zero included — it is
 * the one number a reader looks for on this tile, and its absence would read as "not computed".
 */
export function relationSummaryLine(summary: RelationSummary): string {
    return [
        t("mobileTextOverdueCount", "{count} overdue", { count: summary.overdue }),
        ...summary.states.map((s) => `${s.count} ${s.label}`),
    ].join(" · ");
}

/**
 * What needs this person today, across both relations — overdue before due-today.
 *
 * Capped, because this is the top of the home screen and an uncapped list would
 * push everything else off it. The cap is the caller's, so the list screen can
 * ask for all of them.
 */
export function urgentTodos(todos: readonly Todo[], now: number, limit = 3): Todo[] {
    const bucketOf = (t: Todo) => dueFacts(t.dueISO, now).bucket;
    const rank = (t: Todo) => (bucketOf(t) === "Overdue" ? 0 : 1);

    return todos
        .filter((t) => bucketOf(t) === "Overdue" || bucketOf(t) === "Today")
        .sort((a, b) => rank(a) - rank(b))
        .slice(0, limit);
}

/** "1 thing needs you today" / "4 things need you today". */
export function urgentLabel(count: number): string {
    return count === 1
        ? t("mobileTextUrgentOne", "1 thing needs you today")
        : t("mobileTextUrgentMany", "{count} things need you today", { count });
}

/**
 * Names as a sentence: "Anna", "Anna and Marc", "Anna, Marc and Team Nord".
 *
 * Empty names are left out, so a list with an unresolvable resource in it does not read "Anna and ".
 */
export function namesSentence(names: readonly string[]): string {
    const named = names.map((n) => n.trim()).filter(Boolean);
    if (named.length <= 1) return named[0] ?? "";

    return t("mobileTextNamesAnd", "{names} and {last}", {
        names: named.slice(0, -1).join(", "),
        last: named[named.length - 1]!,
    });
}

/** Every assignee's name, for a row or a fact: "Anna, Marc". `""` when there is nobody to name. */
export function assigneeNames(todo: Todo): string {
    return todo.assignees
        .map((a) => a.name.trim())
        .filter(Boolean)
        .join(", ");
}

/**
 * A group of people as one line, with the READER named "You" — and first.
 *
 * ⚠ THIS IS WHY OWNERS AND ASSIGNEES BOTH CARRY IDS. A TODO may have several of either, and reading
 * somebody else's name beside the owner actions is what made a customer report a permission fault
 * that was not one (see pqfMobileOwnersFrom()). Naming yourself answers "why may I edit this" in the
 * one place the question is asked, and on the assignee row it answers the smaller version of it —
 * "is this mine".
 *
 * ⚠ Matched by RESOURCE ID, never by name: two people share a name often enough, and an account's
 * name need not be spelled like its resource's. Somebody with no id — an older server — matches
 * nobody and is shown by name, which is the honest answer rather than a guess.
 *
 * First, because that is the fact being looked for; the rest keep the server's order.
 *
 * ⚠ It is deliberately NOT used where a name becomes an AVATAR (the list row's counterparty): the
 * initials of "You" are a Y, which says less than the initials of a name.
 */
export function namesWithSelf(people: readonly { id: string; name: string }[], selfResourceId: string): string {
    const mine = selfResourceId !== "" && people.some((p) => p.id === selfResourceId);
    const others = people
        .filter((p) => !(mine && p.id === selfResourceId))
        .map((p) => p.name.trim())
        .filter(Boolean);

    return (mine ? [t("mobileTextYou", "You"), ...others] : others).join(", ");
}

/**
 * Who a nudge reminds: every assignee except the person nudging, and only those with an id.
 *
 * ⚠ The server decides the same thing, and refuses when this is empty — this is what lets the
 * screen not offer a button that can only be refused.
 */
export function nudgeTargets(todo: Todo, selfResourceId: string): Assignee[] {
    return todo.assignees.filter((a) => a.id !== "" && a.id !== selfResourceId);
}

/** "Nudge Anna", "Nudge 3 assignees" — a label that stays short however many there are. */
export function nudgeLabel(targets: readonly Assignee[]): string {
    if (targets.length === 1) {
        return targets[0]!.name
            ? t("mobileTextNudgeOne", "Nudge {name}", { name: targets[0]!.name })
            : t("mobileTextNudgeAssignee", "Nudge the assignee");
    }

    return t("mobileTextNudgeMany", "Nudge {count} assignees", { count: targets.length });
}

/** Why a nudge did not email somebody, as the server reports it. See mylibs/pqforce-mobile-nudge.php. */
export type NudgeSkipReason = "optedOut" | "noAddress" | "noAccount" | "failed";

/** What the server did with a nudge: the resource ids emailed, and everybody it could not mail. */
export interface NudgeResult {
    emailed: string[];
    skipped: Array<{ resourceId: string; reason: NudgeSkipReason }>;
}

/**
 * The toast after a nudge: who was emailed, then who was not and why.
 *
 * ⚠ NAMED FROM THE SERVER'S ANSWER, never from the assignee list — reminding "everybody" when two of
 * three had email notifications off is exactly the claim this replaces. A skip is said per reason,
 * because each has a different remedy: notifications off is the colleague's own choice; a missing
 * address is something an administrator fixes in PQFORCE.
 */
export function nudgeResultText(todo: Todo, result: NudgeResult): string {
    const nameOf = (id: string) => todo.assignees.find((a) => a.id === id)?.name.trim() ?? "";
    const names = (ids: string[]) => namesSentence(ids.map(nameOf));

    const emailed = names(result.emailed);
    const parts = [
        emailed
            ? t("mobileTextNudgeEmailedTo", "Reminder emailed to {names}.", { names: emailed })
            : t("mobileTextNudgeEmailed", "Reminder emailed."),
    ];

    const by = (reason: NudgeSkipReason) => result.skipped.filter((s) => s.reason === reason).map((s) => s.resourceId);
    const optedOut = by("optedOut");
    const noAddress = [...by("noAddress"), ...by("noAccount")];
    const failed = by("failed");

    // "Somebody" stands in for a name the TODO could not resolve — whole sentences, so a translator
    // never has to fit a pronoun into somebody else's grammar.
    if (optedOut.length) {
        const who = names(optedOut);
        parts.push(
            !who
                ? t("mobileTextNudgeOptedOutSomebody", "Somebody has email notifications off.")
                : optedOut.length === 1
                  ? t("mobileTextNudgeOptedOutOne", "{names} has email notifications off.", { names: who })
                  : t("mobileTextNudgeOptedOutMany", "{names} have email notifications off.", { names: who }),
        );
    }
    if (noAddress.length) {
        const who = names(noAddress);
        parts.push(
            who
                ? t("mobileTextNudgeNoAddress", "No email address is stored for {names}.", { names: who })
                : t("mobileTextNudgeNoAddressSomebody", "No email address is stored for somebody."),
        );
    }
    if (failed.length) {
        const who = names(failed);
        parts.push(
            who
                ? t("mobileTextNudgeFailed", "The mail to {names} could not be sent.", { names: who })
                : t("mobileTextNudgeFailedSomebody", "The mail to somebody could not be sent."),
        );
    }

    return parts.join(" ");
}

/**
 * The assignee sheet's two lists: who is assigned, and who could be added.
 *
 * ⚠ "Others" is every offered person NOT already assigned, matched by id. An assignee who is no
 * longer offered (another type, a resource since deleted) still appears under "assigned", so they
 * can be removed — which is what the server's remove allows.
 */
export function assigneeChoices<P extends { id: string; name: string }>(
    todo: Todo,
    people: readonly P[],
): { assigned: Assignee[]; others: P[] } {
    const assignedIds = new Set(todo.assignees.map((a) => a.id));

    return {
        assigned: todo.assignees.filter((a) => a.id !== ""),
        others: people.filter((p) => !assignedIds.has(p.id)),
    };
}

/**
 * Does `haystack` answer `query`?
 *
 * Every word typed must appear somewhere in it, in any order — "müller anna" finds "Anna Müller".
 * Case and accents are ignored, so "muller" finds her too: a phone keyboard makes an umlaut a
 * long-press, and nobody searching should need one. An empty query matches everything.
 *
 * ONE rule for both searches in the app, so the assignees sheet and the TODO list cannot come to
 * disagree about what typing two words means.
 */
function matchesQueryWords(haystack: string, query: string): boolean {
    const fold = (s: string): string => s.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();
    const folded = fold(haystack);

    return fold(query)
        .split(/\s+/)
        .filter(Boolean)
        .every((word) => folded.includes(word));
}

/** Does a person's name answer the assignees sheet's search? See matchesQueryWords(). */
export function personMatchesQuery(name: string, query: string): boolean {
    return matchesQueryWords(name, query);
}

/** The named entities a tenant's rich text actually carries, plus whatever it numbers. */
const HTML_ENTITIES: Record<string, string> = {
    nbsp: " ",
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
};

/**
 * The readable TEXT of a description, for MATCHING ONLY.
 *
 * ⚠ NOT A SANITISER, and nothing it returns is ever put on screen — `richTextHtml()` in `dom.ts`
 * does that, from a tree. This only has to produce a haystack, which is why a regex is honest here
 * and is a defect there: the worst a malformed tag can do is make a word unfindable.
 *
 * ⚠ TAGS FIRST, ENTITIES SECOND. The other order turns a written `&lt;b&gt;` into a tag and then
 * deletes it, losing the words somebody typed — the trap `pqfSanitizeSupportHtml()` records from the
 * other side. Tags become a SPACE, or `one</p><p>two` matches nothing but "onetwo".
 */
function descriptionText(html: string): string {
    if (!html) return "";

    return html.replace(/<[^>]*>/g, " ").replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, body: string) => {
        if (body[0] !== "#") return HTML_ENTITIES[body.toLowerCase()] ?? entity;

        const code =
            body[1] === "x" || body[1] === "X"
                ? Number.parseInt(body.slice(2), 16)
                : Number.parseInt(body.slice(1), 10);
        return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    });
}

/**
 * Does a TODO answer the search? Its CODE, its TITLE and its DESCRIPTION are searched, nothing else.
 *
 * The two are matched as one text, so "tariff review" finds a todo whose title names the tariff and
 * whose description asks for the review — the same "every word, anywhere" rule as above. Deliberately
 * not the state, the project or the people: those are what the segments, the groups and the tiles are
 * for, and a query matching a colleague's name would return every todo they touch.
 */
export function todoMatchesQuery(todo: Todo, query: string): boolean {
    // The CODE too (`T-12`): it is what people say to each other about a TODO, and typing it is the
    // quickest way to find one somebody mentioned.
    return matchesQueryWords(`${todo.code ?? ""} ${todo.title} ${descriptionText(todo.description)}`, query);
}

/** The kinds of link a TODO's facts table shows, one row each — see `linkRows()`. */
export type LinkSlot = "ppm" | "related" | "assignee" | "owner";

/**
 * The slots the APP can change — the portal's half of "may this be changed here", mirroring
 * `pqfMobileLinkSlots()`'s `editable`. PQFORCE's half is `permissions.link`. The PPM link joins
 * this set in the change that brings a projects module and its picker.
 */
export const EDITABLE_LINK_SLOTS: ReadonlySet<LinkSlot> = new Set<LinkSlot>(["assignee", "owner"]);

/**
 * The facts table's rows, in order, and whether each offers a change. One descriptor per slot, so a
 * slot a later module makes editable is a change here and an editor there — not a rewrite of the
 * table.
 *
 * "Related to" is left out when there is nothing related: an empty row would read as a failed load.
 * A change is offered only where the app has an editor AND PQFORCE lets this person link — and never
 * while that is not known yet (no `permissions`), nor on a row still being read whole (`partial`).
 */
export function linkRows(todo: Todo): Array<{ slot: LinkSlot; editable: boolean }> {
    const slots: LinkSlot[] = ["ppm"];
    if ((todo.related ?? []).length > 0) slots.push("related");
    slots.push("assignee", "owner");

    const mayLink = todo.permissions?.link === true && !todo.partial;
    return slots.map((slot) => ({ slot, editable: mayLink && EDITABLE_LINK_SLOTS.has(slot) }));
}

/**
 * Keep the `permissions` already read for a TODO when a LIST arrives that does not carry them.
 *
 * The lists never carry them (one request per row), the detail reads them; without this every list
 * reload — a language change, a create, coming back to the tab — would make the open TODO's controls
 * go inert again. A row that DOES carry permissions wins, being the newer answer. Returns the same
 * array when nothing needed carrying over.
 */
export function withKnownPermissions(todos: readonly Todo[], known: readonly Todo[]): Todo[] {
    const byId = new Map(known.filter((t) => t.permissions).map((t) => [t.id, t.permissions!]));
    if (byId.size === 0) return todos as Todo[];

    let changed = false;
    const out = todos.map((todo) => {
        const permissions = byId.get(todo.id);
        if (todo.permissions || !permissions) return todo;
        changed = true;
        return { ...todo, permissions };
    });

    return changed ? out : (todos as Todo[]);
}

/**
 * What the header's search searches, for the screen it was opened on.
 *
 * ⚠ ONE MODULE TODAY, so one answer: Home, the TODO list and a TODO are all about TODOs. It is a
 * function of the state rather than a constant so a second module (the tab bar's next item) adds a
 * branch here and a context in the search card, and nothing else has to learn the difference.
 */
export type SearchContext = "todos";

export function searchContextOf(_state: { tab: string }): SearchContext {
    return "todos";
}

/**
 * The TODOs the search card lists for `query`: every TODO this person holds, either way — the card
 * is for finding one, and which segment it sits under is the list's business. Nothing for an empty
 * query (the card says what it searches instead of listing everything).
 *
 * Order: overdue and dated first, soonest due first, undated last, then by title — the TODO
 * somebody is looking for is likelier to be the one that needs them.
 */
export function searchTodos(todos: readonly Todo[], query: string): Todo[] {
    if (!query.trim()) return [];
    return todos
        .filter((todo) => todoMatchesQuery(todo, query))
        .sort((a, b) => {
            if (a.dueISO !== b.dueISO) {
                if (!a.dueISO) return 1;
                if (!b.dueISO) return -1;
                return a.dueISO < b.dueISO ? -1 : 1;
            }
            return a.title.localeCompare(b.title);
        });
}

/**
 * Fill a state's missing icon from wherever the SAME state appears with one.
 *
 * ⚠ A TODO'S OWN STATE CAN ARRIVE WITHOUT AN ICON WHILE THE SAME STATE, AS A TRANSITION TARGET, HAS
 * ONE. Reported on dev (2026-09-23): the list rows showed initials while the detail screen's move
 * buttons showed icons. The two are read from different PQFORCE fields — `state.iconRef` on the item,
 * `possibleLifecycleTransitions[].to.iconRef` on each transition — and on that tenant only the second
 * carried an `mdi:` name. Not reproducible on develop.pqforce.com/dani, where both do.
 *
 * Matched by STATE ID, never by name: a name is the tenant's wording, and two lifecycles may share
 * one. Sources, in order: every todo's own icon, every transition target's, then `known` — the rows
 * already on screen — so a todo re-read after a write (one item, whose state is often not a target
 * of its own transitions) keeps the icon its row had. An icon that did arrive is never replaced.
 *
 * Returns the SAME array when nothing needed filling, so an unchanged list stays referentially
 * unchanged.
 */
export function withKnownStateIcons(todos: readonly Todo[], known: readonly Todo[] = []): Todo[] {
    const icons = new Map<string, StateIcon>();
    const learn = (id: string | undefined, icon: StateIcon | null | undefined): void => {
        if (id && icon?.d && !icons.has(id)) icons.set(id, icon);
    };
    for (const list of [todos, known]) {
        for (const todo of list) {
            learn(todo.statusId, todo.statusIcon);
            for (const transition of todo.transitions) learn(transition.toId, transition.toIcon);
        }
    }

    let changed = false;
    const filled = todos.map((todo) => {
        // The icon to ADD, or undefined — one that arrived is never replaced.
        const statusIcon = todo.statusIcon?.d ? undefined : icons.get(todo.statusId);
        let transitionsChanged = false;
        const transitions = todo.transitions.map((transition) => {
            const toIcon = transition.toIcon?.d || !transition.toId ? undefined : icons.get(transition.toId);
            if (!toIcon) return transition;
            transitionsChanged = true;
            return { ...transition, toIcon };
        });
        if (!statusIcon && !transitionsChanged) return todo;

        changed = true;
        return {
            ...todo,
            ...(statusIcon ? { statusIcon } : {}),
            ...(transitionsChanged ? { transitions } : {}),
        };
    });

    return changed ? filled : (todos as Todo[]);
}

/** On a row, the other party: an assignee sees the owner, an owner sees the assignees. */
export function counterparty(todo: Todo): { label: string; name: string } {
    return todo.relation === "assignee"
        ? {
              // ⚠ Plural when there are several, like the assignee side below it — a TODO may have
              // several owners, and "Owner: Anna, Marc" says one of them is called both names.
              // The tenant's own name for the role wins (relationNames.ts); the app's word otherwise.
              label: relationName(
                  "owner",
                  todo.owners.length > 1 ? t("mobileTextOwners", "Owners") : t("mobileTextOwner", "Owner"),
              ),
              name: todo.owner,
          }
        : {
              label: relationName(
                  "assignee",
                  todo.assignees.length > 1
                      ? t("mobileTextAssignees", "Assignees")
                      : t("mobileTextAssignee", "Assignee"),
              ),
              name: assigneeNames(todo),
          };
}

/** A compact row (the search results) says which side of a todo you are on — by the tenant's list names. */
export function roleLabel(todo: Todo): string {
    return todo.relation === "assignee"
        ? relationListName("assignee", t("mobileTextRoleYours", "Yours"))
        : relationListName("owner", t("mobileTextRoleYouOwn", "You own"));
}

/**
 * Apply a fired transition locally.
 *
 * Returns a NEW list; the activity entry records the move exactly as the design
 * draws it (from → to, with the note). The caller is responsible for having the
 * server accept it first — see `ui_mobile_todos.ts`, which does not call this
 * until the request resolves, because a lifecycle move is not something to show
 * as done and then take back.
 */
/**
 * ⚠ NOT ON THE LIVE PATH. Every write in `ui_mobile_shell.ts` re-reads the todo from the server and
 * calls `store.replaceTodo()`, which is the better answer — the tenant decides what the item became
 * and which transitions are offered next. These two are kept as the optimistic fallback they were
 * written to be; note that neither carries the endpoints' categories or colours, so wiring one up
 * would paint a history entry with a neutral chip until the next read.
 */
export function applyTransition(
    todos: readonly Todo[],
    todoId: string,
    to: string,
    note: string,
    who: string,
    now: number,
    nextTransitions: LifecycleTransition[],
): Todo[] {
    return todos.map((t) =>
        t.id === todoId
            ? {
                  ...t,
                  status: to,
                  transitions: nextTransitions,
                  comments: [...t.comments, { who, ts: now, from: t.status, to, text: note.trim() }],
              }
            : t,
    );
}

/** Append a plain comment. Same shape as a change, minus the state move. */
export function applyComment(todos: readonly Todo[], todoId: string, text: string, who: string, now: number): Todo[] {
    const trimmed = text.trim();
    if (!trimmed) return [...todos];
    return todos.map((t) =>
        t.id === todoId ? { ...t, comments: [...t.comments, { who, ts: now, text: trimmed }] } : t,
    );
}

// ── the PPM link ─────────────────────────────────────────────────────────────
//
// ⚠ ONE LINK OR NONE. A TODO reaches the PPM module through exactly one relation
// — a portfolio, a project or a task — and PQFORCE's own client guarantees no
// more than one exists. The server resolves which, and the levels above it come
// along as context; this is only the wording.

/** The word for a kind. Deliberately the reader's word: PQFORCE's `Phase` is a task. */
export function ppmKindLabel(kind: PpmKind): string {
    switch (kind) {
        case "task":
            return t("mobileTextKindTask", "Task");
        case "project":
            return t("mobileTextKindProject", "Project");
        default:
            return t("mobileTextKindPortfolio", "Portfolio");
    }
}

/**
 * The levels ABOVE the link, as one line.
 *
 * Empty for a portfolio link, and for anything the server could not name — a missing context is an
 * omission, never a dash, so the row shrinks rather than showing a hole.
 */
export function ppmContextLine(ppm: PpmLink | null): string {
    if (!ppm) return "";
    const above = [ppm.project, ppm.portfolio].filter((name) => !!name);

    return above.length ? t("mobileTextInContext", "in {context}", { context: above.join(" · ") }) : "";
}

/**
 * The link on ONE line, for a list row.
 *
 * ⚠ Absence is STATED, not blank. "No project link" is a fact about the todo; an empty sub-line
 * reads as a rendering fault, and this is the line that used to say `${project} · ${task}` and said
 * " · —" on every todo the server could not resolve.
 *
 * A link whose name is unknown falls back to its kind, so the row still says the todo is attached
 * to something.
 */
export function ppmRowText(ppm: PpmLink | null): string {
    if (!ppm) return t("mobileTextNoProjectLink", "No project link");

    const head = ppm.name || ppmKindLabel(ppm.kind);
    const context = ppm.project || ppm.portfolio;

    return context ? `${head} · ${context}` : head;
}

/** The word for a related kind. The reader's word, like ppmKindLabel(). */
export function relatedKindLabel(kind: RelatedKind): string {
    switch (kind) {
        case "risk":
            return t("mobileTextKindRisk", "Risk");
        case "projectReport":
            return t("mobileTextKindProjectReport", "Project report");
        default:
            return t("mobileTextKindPortfolioReport", "Portfolio report");
    }
}

/**
 * The levels a related object sits under, as one line — the indirect route into PPM.
 *
 * Same shape as ppmContextLine(), and deliberately the same wording: a reader should not have to
 * learn that "in X" means one thing under a project link and another under a risk.
 */
export function relatedContextLine(link: RelatedLink): string {
    const above = [link.project, link.portfolio].filter((name) => !!name);

    return above.length ? t("mobileTextInContext", "in {context}", { context: above.join(" · ") }) : "";
}

/**
 * The line a list row gets for what a TODO is attached to.
 *
 * ⚠ Prefers the PPM link, then the first related one. A TODO with no project but a risk would
 * otherwise read "No project link" while being plainly attached to something — which is the same
 * kind of false blank the em dash used to be.
 */
export function todoLinkLine(todo: Todo): string {
    if (todo.ppm) return ppmRowText(todo.ppm);

    const first = (todo.related ?? [])[0];
    if (!first) return ppmRowText(null);

    const head = first.name || relatedKindLabel(first.kind);
    const context = first.project || first.portfolio;

    return context ? `${head} · ${context}` : head;
}
